<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import ImageReview from "../imageReviewComponents/ImageReview.vue";
import Artwork from "../artworkComponents/Artwork.vue";
import Preview from "../previewsComponents/Preview.vue";

const props = defineProps({
  availablePlacements: {
    type: Array,
    required: true
  },
  titlePrefix: {
    type: String,
    required: true
  },
  sideKey: {
    type: String,
    required: true
  },
  isPreview: {
    type: Boolean,
    default: false
  }
});

const store = useStore();
const artworkExists = computed(() => store.getters[`artwork_${props.sideKey}/artworkExists`]);
const artworkTouched = computed(() => store.getters[`artwork_${props.sideKey}/touched`]);
const hideReviewSection = computed(() => {
  if (props.isPreview) return true;
  if (artworkTouched.value) return false;
  return !artworkExists.value;
});
</script>

<template>
  <div class="placement" :class="{ 'placement--preview': isPreview }" data-testid="placement">
    <div class="placement__artwork">
      <Artwork
        :side-key="props.sideKey"
        :is-preview="props.isPreview"
        :available-placements="props.availablePlacements"
        :title-prefix="props.titlePrefix"
      />
    </div>

    <template v-if="!hideReviewSection">
      <div class="placement__review" data-testid="placement-review">
        <ImageReview :title-prefix="props.titlePrefix" :side-key="props.sideKey" />
      </div>

      <div class="placement__preview hidden" data-testid="placement-preview">
        <Preview :side-key="props.sideKey" />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/mixin";

.placement {
  $this: &;

  display: grid;
  grid-template-areas: "artwork" "review" "preview";
  grid-template-columns: 100%;

  @include with-container(container, $pod-pdp-desktop-response) {
    grid-template-areas: "artwork review" "preview preview";
    grid-template-columns: calc(50% - 16px) 50%;
    grid-column-gap: 16px;
  }

  &--preview {
    display: block;

    #{$this}__artwork {
      margin-bottom: 0;
    }
  }

  &__artwork {
    grid-area: artwork;
    margin-bottom: 44px;

    @include with-container(container, $pod-pdp-desktop-response) {
      margin-bottom: 30px;
    }
  }

  &__review {
    grid-area: review;
    margin-bottom: 24px;

    @include with-container(container, $pod-pdp-desktop-response) {
      margin-bottom: 30px;
    }
  }

  &__preview {
    grid-area: preview;
    margin-bottom: 44px;
  }
}
</style>
